import * as React from "react";
import { Box, Tooltip, type PopperProps, type TooltipProps } from "@mui/material";

type Axis = "x" | "y" | "both";

type OverflowTooltipProps = {
  title?: React.ReactNode | ((el: HTMLElement | null) => React.ReactNode);
  children: React.ReactNode;
  placement?: PopperProps["placement"];
  axis?: Axis;
  getTarget?: (wrapperEl: HTMLElement) => HTMLElement | null;
  tooltipProps?: Omit<TooltipProps, "title" | "children" | "open">;
};

const OverflowTooltip: React.FC<OverflowTooltipProps> = ({
  title,
  children,
  placement = "top",
  axis = "x",
  getTarget,
  tooltipProps,
}) => {
  const wrapperRef = React.useRef<HTMLElement | null>(null);
  const textRef = React.useRef<HTMLElement | null>(null);

  const [show, setShow] = React.useState(false);
  const [hover, setHover] = React.useState(false);

  const isTextChild = typeof children === "string" || typeof children === "number";

  const resolveTarget = React.useCallback((): HTMLElement | null => {
    const w = wrapperRef.current;
    return (w && getTarget?.(w)) || textRef.current || w;
  }, [getTarget]);

  const checkOverflow = React.useCallback(() => {
    const el = resolveTarget();
    if (!el) return;

    const overflowX = el.scrollWidth > el.clientWidth;
    const overflowY = el.scrollHeight > el.clientHeight;

    setShow(axis === "x" ? overflowX : axis === "y" ? overflowY : overflowX || overflowY);
  }, [resolveTarget, axis]);

  React.useLayoutEffect(() => {
    const el = resolveTarget();
    if (!el) return;

    checkOverflow();

    const ro = new ResizeObserver(checkOverflow);
    ro.observe(el);

    return () => ro.disconnect();
  }, [children, resolveTarget, checkOverflow]);

  React.useEffect(() => {
    const el = resolveTarget();
    if (!el) return;

    const handler = () => checkOverflow();
    el.addEventListener("input", handler);
    el.addEventListener("keyup", handler);

    return () => {
      el.removeEventListener("input", handler);
      el.removeEventListener("keyup", handler);
    };
  }, [resolveTarget, checkOverflow]);

  const resolvedTitle =
    typeof title === "function"
      ? title(resolveTarget())
      : (title ?? (isTextChild ? String(children) : ""));

  const content = isTextChild ? (
    <span
      ref={textRef as any}
      style={{
        display: "inline-block",
        maxWidth: "100%",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        verticalAlign: "bottom",
      }}
    >
      {children as any}
    </span>
  ) : (
    children
  );

  return (
    <Tooltip
      placement={placement}
      title={resolvedTitle}
      open={hover && show}
      slotProps={{ transition: { timeout: { enter: 150, exit: 0 } } }}
      {...tooltipProps}
    >
      <Box
        ref={wrapperRef}
        onMouseEnter={() => {
          setHover(true);
          checkOverflow();
        }}
        onMouseLeave={() => setHover(false)}
        sx={{
          width: "100%",
          minWidth: 0,
          maxWidth: "100%",
        }}
      >
        {content}
      </Box>
    </Tooltip>
  );
};

export default OverflowTooltip;
